import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Plus, Download, Filter, FileText, Eye, Edit, Trash2 } from "lucide-react";
import { supabase } from "../../../../src/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PDFViewer } from "@/components/ui/pdf-viewer";

interface MagazinesManagerProps {
  userRegion?: string;
  isGlobalAdmin: boolean;
}

interface Magazine {
  id: string;
  title: string;
  year: number;
  start_month: number;
  end_month: number;
  pdf_path: string;
  published: boolean;
  created_at: string;
}

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const MagazinesManager: React.FC<MagazinesManagerProps> = ({
  userRegion,
  isGlobalAdmin,
}) => {
  const [magazines, setMagazines] = useState<Magazine[]>([]);
  const [viewingPdf, setViewingPdf] = useState<{ url: string; title: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [yearFilter, setYearFilter] = useState("all");
  const [startMonthFilter, setStartMonthFilter] = useState("all");
  const [endMonthFilter, setEndMonthFilter] = useState("all");
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    year: new Date().getFullYear(),
    start_month: 1,
    end_month: 12,
    published: true,
  });
  const [file, setFile] = useState<File | null>(null);
  const [editingMagazine, setEditingMagazine] = useState<Magazine | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [magazineToDelete, setMagazineToDelete] = useState<Magazine | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewingPdfUrl, setViewingPdfUrl] = useState<string>("");
  const [deleteCurrentAttachment, setDeleteCurrentAttachment] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchMagazines();
  }, [yearFilter, startMonthFilter, endMonthFilter, statusFilter]);

  const fetchMagazines = async () => {
    setLoading(true);
    
    let query = supabase
      .from('magazines')
      .select('*')
      .order('year', { ascending: false })
      .order('start_month', { ascending: false });

    if (yearFilter !== "all") {
      query = query.eq('year', parseInt(yearFilter));
    }

    if (startMonthFilter !== "all") {
      query = query.gte('start_month', parseInt(startMonthFilter));
    }

    if (endMonthFilter !== "all") {
      query = query.lte('end_month', parseInt(endMonthFilter));
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching magazines:', error);
      toast({
        title: "Error",
        description: "Failed to fetch magazines",
        variant: "destructive",
      });
    } else {
      setMagazines(data || []);
    }
    
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingMagazine && !file) {
      toast({
        title: "Error",
        description: "Please select a PDF file",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      let pdfPath = editingMagazine?.pdf_path || '';

      // Upload new file if provided
      if (file) {
        const fileName = `magazines/${Date.now()}_${file.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from("general-media")
          .upload(fileName, file);

        if (uploadError) throw uploadError;
        pdfPath = uploadData.path;

        // Delete old file if editing
        if (editingMagazine?.pdf_path) {
          await supabase.storage.from("general-media").remove([editingMagazine.pdf_path]);
        }
      }

      if (editingMagazine) {
        // Update existing magazine
        const { error: dbError } = await supabase
          .from("magazines")
          .update({
            title: formData.title,
            year: formData.year,
            start_month: formData.start_month,
            end_month: formData.end_month,
            pdf_path: pdfPath,
          })
          .eq("id", editingMagazine.id);

        if (dbError) throw dbError;
        toast({
          title: "Success!",
          description: "Magazine has been updated successfully.",
        });
      } else {
        // Create new magazine
        const { error: dbError } = await supabase
          .from("magazines")
          .insert([
            {
              title: formData.title,
              year: formData.year,
              start_month: formData.start_month,
              end_month: formData.end_month,
              pdf_path: pdfPath,
              published: formData.published,
            },
          ]);

        if (dbError) throw dbError;
        toast({
          title: "Success!",
          description: "Magazine has been uploaded successfully.",
        });
      }

      // Reset form
      setFormData({
        title: "",
        year: new Date().getFullYear(),
        start_month: 1,
        end_month: 12,
        published: true,
      });
      setFile(null);
      setEditingMagazine(null);
      setDialogOpen(false);
      fetchMagazines();
    } catch (error) {
      console.error("Error saving magazine:", error);
      toast({
        title: "Error",
        description: "Failed to save magazine. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (magazine: Magazine) => {
    setEditingMagazine(magazine);
    setFormData({
      title: magazine.title,
      year: magazine.year,
      start_month: magazine.start_month,
      end_month: magazine.end_month,
      published: magazine.published,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (magazine: Magazine) => {
    setMagazineToDelete(magazine);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!magazineToDelete) return;

    try {
      // Delete file from storage
      await supabase.storage.from("general-media").remove([magazineToDelete.pdf_path]);

      // Delete record from database
      const { error } = await supabase
        .from("magazines")
        .delete()
        .eq("id", magazineToDelete.id);

      if (error) throw error;

      toast({
        title: "Success!",
        description: "Magazine has been deleted successfully.",
      });

      setDeleteDialogOpen(false);
      setMagazineToDelete(null);
      fetchMagazines();
    } catch (error) {
      console.error("Error deleting magazine:", error);
      toast({
        title: "Error",
        description: "Failed to delete magazine.",
        variant: "destructive",
      });
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      year: new Date().getFullYear(),
      start_month: 1,
      end_month: 12,
      published: true,
    });
    setFile(null);
    setEditingMagazine(null);
    setDeleteCurrentAttachment(false);
  };

  const viewMagazine = async (magazine: Magazine) => {
    try {
      const { data } = supabase.storage
        .from('general-media')
        .getPublicUrl(magazine.pdf_path);

      if (data?.publicUrl) {
        setViewingPdf({ url: data.publicUrl, title: magazine.title });
      }
    } catch (error) {
      console.error('Error viewing magazine:', error);
      toast({
        title: "Error",
        description: "Failed to view magazine",
        variant: "destructive",
      });
    }
  };

  const downloadMagazine = async (magazine: Magazine) => {
    try {
      const { data, error } = await supabase.storage
        .from('general-media')
        .download(magazine.pdf_path);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${magazine.title}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({
        title: "Success",
        description: "Magazine downloaded successfully",
      });
    } catch (error) {
      console.error('Error downloading magazine:', error);
      toast({
        title: "Error",
        description: "Failed to download magazine",
        variant: "destructive",
      });
    }
  };

  const getMonthRange = (startMonth: number, endMonth: number) => {
    const start = MONTHS.find(m => m.value === startMonth)?.label || '';
    const end = MONTHS.find(m => m.value === endMonth)?.label || '';
    
    if (startMonth === endMonth) {
      return start;
    }
    return `${start} - ${end}`;
  };

  const getAvailableYears = () => {
    const years = [...new Set(magazines.map(m => m.year))].sort((a, b) => b - a);
    return years;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Add Magazine
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingMagazine ? "Edit Magazine" : "Add New Magazine"}</DialogTitle>
                <DialogDescription>
                  {editingMagazine ? "Update magazine details." : "Upload a new magazine PDF with details."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Magazine Title</Label>
                  <Input
                    id="title"
                    placeholder="Enter magazine title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="year">Year</Label>
                  <Input
                    id="year"
                    type="number"
                    min="1900"
                    max="2100"
                    value={formData.year}
                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_month">Start Month</Label>
                    <Select
                      value={formData.start_month.toString()}
                      onValueChange={(value) => setFormData({ ...formData, start_month: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((month) => (
                          <SelectItem key={month.value} value={month.value.toString()}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_month">End Month</Label>
                    <Select
                      value={formData.end_month.toString()}
                      onValueChange={(value) => setFormData({ ...formData, end_month: parseInt(value) })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((month) => (
                          <SelectItem key={month.value} value={month.value.toString()}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="pdf">Attachment</Label>
                  
                  {editingMagazine && !deleteCurrentAttachment && !file && (
                    <div className="flex items-center justify-between p-3 bg-muted rounded-lg mb-2">
                      <div className="flex items-center space-x-2">
                        <FileText className="h-4 w-4 text-primary" />
                        <div>
                          <p className="text-sm font-medium">Current Attachment</p>
                          <p className="text-xs text-muted-foreground">
                            {editingMagazine.pdf_path.split('/').pop()}
                          </p>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => setDeleteCurrentAttachment(true)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  {editingMagazine && deleteCurrentAttachment && !file && (
                    <div className="p-3 bg-destructive/10 border border-destructive rounded-lg mb-2">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-destructive font-medium">
                          Current attachment will be deleted
                        </p>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteCurrentAttachment(false)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Please upload a new attachment
                      </p>
                    </div>
                  )}

                  <Input
                    id="pdf"
                    type="file"
                    accept=".pdf"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    required={!editingMagazine || deleteCurrentAttachment}
                  />
                  
                  {file && (
                    <div className="flex items-center space-x-2 p-2 bg-primary/10 border border-primary rounded">
                      <FileText className="h-4 w-4 text-primary" />
                      <span className="text-sm font-medium">New file: {file.name}</span>
                    </div>
                  )}
                </div>

                <Button type="submit" disabled={uploading} className="w-full">
                  {uploading ? (editingMagazine ? "Updating..." : "Uploading...") : (editingMagazine ? "Update Magazine" : "Upload Magazine")}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="published">Published</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Filter by Year</Label>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Years" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {getAvailableYears().map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Start Month (From)</Label>
              <Select value={startMonthFilter} onValueChange={setStartMonthFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {MONTHS.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>End Month (To)</Label>
              <Select value={endMonthFilter} onValueChange={setEndMonthFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All Months" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Months</SelectItem>
                  {MONTHS.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Magazines ({magazines.filter(m => statusFilter === 'all' || (statusFilter === 'published' ? m.published : !m.published)).length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading magazines...</div>
          ) : magazines.filter(m => statusFilter === 'all' || (statusFilter === 'published' ? m.published : !m.published)).length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No magazines found. Click "Add Magazine" to upload one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Month Range</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {magazines.filter(m => statusFilter === 'all' || (statusFilter === 'published' ? m.published : !m.published)).map((magazine) => (
                    <TableRow key={magazine.id}>
                      <TableCell className="font-medium">{magazine.title}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{magazine.year}</Badge>
                      </TableCell>
                      <TableCell>
                        {getMonthRange(magazine.start_month, magazine.end_month)}
                      </TableCell>
                      <TableCell>
                        <Badge variant={magazine.published ? "default" : "secondary"}>
                          {magazine.published ? "Published" : "Draft"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => viewMagazine(magazine)} title="View">
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(magazine)} title="Edit">
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => downloadMagazine(magazine)} title="Download">
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => openDeleteDialog(magazine)} title="Delete">
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the magazine
              "{magazineToDelete?.title}" and remove it from our servers.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* PDF Viewer Dialog */}
      <Dialog open={!!viewingPdf} onOpenChange={(open) => !open && setViewingPdf(null)}>
        <DialogContent className="max-w-6xl h-[90vh] p-0">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle>{viewingPdf?.title}</DialogTitle>
          </DialogHeader>
          {viewingPdf && (
            <div className="h-[calc(90vh-80px)]">
              <PDFViewer
                url={viewingPdf.url}
                fileName={`${viewingPdf.title}.pdf`}
                dialogOpen={!!viewingPdf}
                debugLabel="Admin Magazine PDF Viewer"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

    </div>
  );
};